import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatConversation, ChatMessage, User } from '../../types';
import { api } from '../../services/apiService';
import { useChatStream, ChatStreamEvent } from '../../services/useChatStream';
import MessageBubble from './MessageBubble';
import { CHAT_UNREAD_EVENT } from './ChatBadge';

interface Props {
  conversation: ChatConversation;
  currentUser: User;
  onMessageSent?: () => void;   // notifier le parent pour refresh liste (last_message)
  /** Phase 3 R3 — si fourni, scroll vers ce message dès que les messages
   *  sont chargés ou que ce message arrive via SSE. Flash highlight visuel. */
  scrollToMessageId?: string | null;
  /** Appelé une fois le scroll consommé. */
  onScrollConsumed?: () => void;
}

// Polling de filet de sécurité — SSE pousse les nouveaux messages en temps
// réel, ce polling rattrape les réactions (pas de push SSE pour ça en MVP)
// et garantit la cohérence si une déconnexion réseau a fait rater des events.
const FALLBACK_POLL_MS = 15000;

interface PendingAttachment {
  id: string;
  original_name: string;
  size_bytes: number;
}

const ConversationView: React.FC<Props> = ({ conversation, currentUser, onMessageSent, scrollToMessageId, onScrollConsumed }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Édition : si non-null, message en cours d'édition
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  // Pièces jointes en attente d'être envoyées avec le prochain message
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  // Phase 3 R2 — typing + presence en temps réel
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});   // user_id → name
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(() => {
    // Initialiser depuis le snapshot de la conv (chat_list_conversations.participants[].online)
    const init = new Set<string>();
    conversation.participants.forEach(p => { if (p.online) init.add(p.user_id); });
    return init;
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  // Throttle du typing ping (max 1 par 2 s) + auto-stop après inactivité
  const lastTypingPingRef = useRef<number>(0);

  // Charger les messages — pagination cursor-based (sans cursor = derniers 50)
  const loadMessages = useCallback(async (initial = false) => {
    try {
      const res = await api.chatListMessages(conversation.id);
      // L'API renvoie en DESC, on inverse pour affichage chronologique (vieux → récent)
      const ordered = [...res.messages].reverse();
      setMessages(ordered);
      setError(null);
      // Scroll en bas au chargement initial ou si nouveau message
      const lastId = ordered.length > 0 ? ordered[ordered.length - 1].id : null;
      if (initial || (lastId && lastId !== lastMessageIdRef.current)) {
        lastMessageIdRef.current = lastId;
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      }
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
    } finally {
      if (initial) setLoading(false);
    }
  }, [conversation.id]);

  // Reset au changement de conversation
  useEffect(() => {
    setMessages([]);
    setLoading(true);
    setInput('');
    lastMessageIdRef.current = null;
    loadMessages(true);
  }, [conversation.id, loadMessages]);

  // Phase 3 R3 — scroll vers un message ciblé (recherche globale Ctrl+K)
  useEffect(() => {
    if (!scrollToMessageId || loading) return;
    if (!messages.some(m => m.id === scrollToMessageId)) return;
    // Délai pour laisser le DOM rendre
    const t = window.setTimeout(() => {
      const el = document.getElementById(`chat-msg-${scrollToMessageId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Flash highlight 1.5s
        el.classList.add('chat-msg-flash');
        window.setTimeout(() => el.classList.remove('chat-msg-flash'), 1500);
      }
      onScrollConsumed?.();
    }, 80);
    return () => window.clearTimeout(t);
  }, [scrollToMessageId, loading, messages, onScrollConsumed]);

  // ----- SSE temps réel (Phase 3) -----
  // Push instantané des nouveaux messages, édits et suppressions.
  // Les réactions ne sont pas pushées en MVP — capturées par le polling de
  // fallback ci-dessous et par l'optimistic update de chatReact.
  const onStreamEvent = useCallback((event: ChatStreamEvent) => {
    // Pour les events qui transportent un message complet, on exige la payload.
    // Les events typing_* / presence_* portent user_id à la place, donc on
    // les laisse passer ici.
    if (event.type === 'created' || event.type === 'edited' || event.type === 'deleted') {
      if (!event.message) return;
    }
    if (event.type === 'created') {
      const incoming = event.message!;
      setMessages(prev => {
        if (prev.some(m => m.id === incoming.id)) return prev; // déjà là (optimistic)
        const next = [...prev, incoming];
        // Scroll en bas si l'utilisateur est déjà près du bas
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            const el = scrollRef.current;
            const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 200;
            if (nearBottom) el.scrollTop = el.scrollHeight;
          }
        });
        return next;
      });
      // Auto-mark-read si message reçu d'un autre + conv visible (onglet actif)
      // Sinon : le badge s'incrémente côté navbar et l'user verra la notif.
      if (incoming.sender_id !== currentUser.id && !document.hidden) {
        api.chatMarkRead(conversation.id)
          .then(() => window.dispatchEvent(new Event(CHAT_UNREAD_EVENT)))
          .catch(() => { /* non-bloquant */ });
      } else if (incoming.sender_id !== currentUser.id) {
        // Onglet pas actif → laisser le badge s'incrémenter
        window.dispatchEvent(new Event(CHAT_UNREAD_EVENT));
      }
    } else if (event.type === 'edited') {
      setMessages(prev => prev.map(m =>
        m.id === event.message!.id
          ? { ...m, content: event.message!.content, edited_at: event.message!.edited_at }
          : m
      ));
    } else if (event.type === 'deleted') {
      setMessages(prev => prev.map(m =>
        m.id === event.message!.id
          ? { ...m, deleted_at: event.message!.deleted_at, content: '' }
          : m
      ));
    } else if (event.type === 'typing_started' && event.user_id) {
      const uid = event.user_id;
      const name = event.user_name || uid;
      setTypingUsers(prev => ({ ...prev, [uid]: name }));
    } else if (event.type === 'typing_stopped' && event.user_id) {
      const uid = event.user_id;
      setTypingUsers(prev => {
        if (!(uid in prev)) return prev;
        const { [uid]: _removed, ...rest } = prev;
        return rest;
      });
    } else if (event.type === 'presence_online' && event.user_id) {
      const uid = event.user_id;
      setOnlineUsers(prev => {
        if (prev.has(uid)) return prev;
        const next = new Set(prev); next.add(uid); return next;
      });
    } else if (event.type === 'presence_offline' && event.user_id) {
      const uid = event.user_id;
      setOnlineUsers(prev => {
        if (!prev.has(uid)) return prev;
        const next = new Set(prev); next.delete(uid); return next;
      });
    }
    // 'ping' / 'ready' / 'bye' : pas d'action côté UI
  }, [currentUser.id, conversation.id]);
  useChatStream(conversation.id, onStreamEvent);

  // Polling de fallback (15 s) — rattrape les réactions + sécurise contre
  // une coupure SSE silencieuse. Pause si l'onglet est caché.
  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (!stopped && !document.hidden) {
        loadMessages(false).catch(() => {});
      }
    };
    const t = window.setInterval(tick, FALLBACK_POLL_MS);
    return () => { stopped = true; window.clearInterval(t); };
  }, [loadMessages]);

  // Envoyer un message (avec pièces jointes éventuelles)
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = input.trim();
    // Un message sans contenu n'est valide que s'il y a au moins une pièce jointe
    if ((!content && pendingAttachments.length === 0) || sending) return;
    if (content.length > 4000) {
      setError('Message trop long (4000 caractères max)');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const finalContent = content || '📎 (fichier joint)';
      const ids = pendingAttachments.map(a => a.id);
      const sent = await api.chatSendMessage(conversation.id, finalContent, ids.length ? ids : undefined);
      // Optimistic update — on ajoute le message en local sans attendre le poll
      setMessages(prev => [...prev, {
        ...sent,
        sender_name: currentUser.name,
        edited_at: null,
        deleted_at: null,
        attachments: pendingAttachments.map(a => ({
          id: a.id, original_name: a.original_name,
          mime_type: '', size_bytes: a.size_bytes,
        })),
      }]);
      setInput('');
      setPendingAttachments([]);
      onMessageSent?.();
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // ----- Upload de pièce jointe (Phase 2) -----
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      const uploads: PendingAttachment[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (pendingAttachments.length + uploads.length >= 10) {
          setError('Maximum 10 pièces jointes par message');
          break;
        }
        const r = await api.chatUploadAttachment(conversation.id, f);
        uploads.push({ id: r.id, original_name: r.original_name, size_bytes: r.size_bytes });
      }
      setPendingAttachments(prev => [...prev, ...uploads]);
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePendingAttachment = (id: string) => {
    setPendingAttachments(prev => prev.filter(a => a.id !== id));
  };

  // Phase 3 R2 — ping typing throttled (max 1 / 2 s). Le serveur considère
  // qu'un user a arrêté de taper après 5 s sans ping (cf. chat_stream).
  const notifyTyping = useCallback(() => {
    if (conversation.my_role === 'observer') return;
    const now = Date.now();
    if (now - lastTypingPingRef.current < 2000) return;
    lastTypingPingRef.current = now;
    api.chatTypingPing(conversation.id).catch(() => { /* best-effort */ });
  }, [conversation.id, conversation.my_role]);

  // ----- Édition d'un message -----
  const handleEditRequest = (messageId: string, currentContent: string) => {
    setEditingMessageId(messageId);
    setEditingDraft(currentContent);
    setError(null);
  };

  const handleEditCancel = () => {
    setEditingMessageId(null);
    setEditingDraft('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessageId) return;
    const content = editingDraft.trim();
    if (!content) {
      setError('Le message ne peut pas être vide');
      return;
    }
    try {
      await api.chatEditMessage(editingMessageId, content);
      // Optimistic update
      setMessages(prev => prev.map(m =>
        m.id === editingMessageId
          ? { ...m, content, edited_at: new Date().toISOString().slice(0, 19).replace('T', ' ') }
          : m
      ));
      setEditingMessageId(null);
      setEditingDraft('');
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l\'édition');
    }
  };

  // ----- Suppression d'un message (soft delete) -----
  const handleDelete = async (messageId: string) => {
    if (!window.confirm('Supprimer ce message ? Cette action est définitive.')) return;
    try {
      await api.chatDeleteMessage(messageId);
      // Optimistic update : marquer comme supprimé
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, deleted_at: new Date().toISOString().slice(0, 19).replace('T', ' '), content: '' }
          : m
      ));
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la suppression');
    }
  };

  // ----- Toggle réaction emoji (Phase 2) -----
  const handleReact = async (messageId: string, emoji: string) => {
    try {
      const { reactions } = await api.chatReact(messageId, emoji);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, reactions } : m
      ));
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la réaction');
    }
  };

  // Titre de la conversation
  const title = useMemo(() => {
    if (conversation.type === 'direct') {
      const other = conversation.participants.find(p => p.user_id !== currentUser.id && p.role === 'member');
      return other?.user_name || 'Conversation';
    }
    return conversation.name || 'Groupe';
  }, [conversation, currentUser.id]);

  const observers = conversation.participants.filter(p => p.role === 'observer');
  const isReadOnly = conversation.my_role === 'observer';

  // Phase 3 R2 — pastille online à côté du nom (conv direct uniquement)
  const otherDirectUser = useMemo(() => {
    if (conversation.type !== 'direct') return null;
    return conversation.participants.find(
      p => p.user_id !== currentUser.id && p.role === 'member'
    ) || null;
  }, [conversation, currentUser.id]);
  const otherIsOnline = otherDirectUser ? onlineUsers.has(otherDirectUser.user_id) : false;

  // "X est en train d'écrire…" — texte combiné si plusieurs
  const typingText = useMemo(() => {
    const names = Object.values(typingUsers);
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} est en train d'écrire…`;
    if (names.length === 2) return `${names[0]} et ${names[1]} sont en train d'écrire…`;
    return `Plusieurs personnes écrivent…`;
  }, [typingUsers]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-[rgba(100,140,210,0.2)] px-4 py-3 flex-shrink-0 bg-white dark:bg-transparent">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-900 dark:text-[#e0eaff] flex items-center gap-2">
              {otherDirectUser && (
                <span
                  className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                    otherIsOnline ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-600'
                  }`}
                  title={otherIsOnline ? 'En ligne' : 'Hors ligne'}
                  aria-label={otherIsOnline ? 'En ligne' : 'Hors ligne'}
                />
              )}
              <span className="truncate">{title}</span>
            </h3>
            <p className="text-[10px] text-slate-500 dark:text-[#7f93bc] mt-0.5">
              {conversation.participants.filter(p => p.role === 'member').length} participant(s)
              {observers.length > 0 && (
                <span className="ml-2" title={observers.map(o => o.user_name).join(', ')}>
                  · 👁️ {observers.length} observateur(s)
                </span>
              )}
              {conversation.is_private && <span className="ml-2">🔒 Privée</span>}
            </p>
            {typingText && (
              <p className="text-[10px] italic text-cyan-600 dark:text-cyan-300 mt-1 animate-pulse">
                {typingText}
              </p>
            )}
          </div>
          {isReadOnly && (
            <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md px-2 py-1 flex-shrink-0">
              Lecture seule (observateur)
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="text-center text-xs text-slate-500 dark:text-[#546a96] py-8">Chargement…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-xs text-slate-500 dark:text-[#546a96] py-8">Aucun message. Soyez le premier à écrire.</div>
        ) : (
          messages.map((m, i) => {
            const isMine = m.sender_id === currentUser.id;
            // Afficher le nom uniquement quand l'expéditeur change ou en début
            const prev = i > 0 ? messages[i - 1] : null;
            const showSender = !prev || prev.sender_id !== m.sender_id;
            const isAdmin = currentUser.role === 'COMPANY_ADMIN' || currentUser.role === 'SUPER_ADMIN';
            return (
              <MessageBubble
                key={m.id}
                message={m}
                isMine={isMine}
                isAdmin={isAdmin}
                showSender={showSender && conversation.type !== 'direct'}
                onEdit={handleEditRequest}
                onDelete={handleDelete}
                onReact={isReadOnly ? undefined : handleReact}
              />
            );
          })
        )}
      </div>

      {/* Bannière d'édition (overlay au-dessus de l'input) */}
      {editingMessageId && (
        <form onSubmit={handleEditSubmit} className="border-t border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-widest">✏️ Édition du message</span>
            <button type="button" onClick={handleEditCancel}
              className="text-[10px] font-bold text-slate-500 dark:text-[#7f93bc] hover:text-slate-900 dark:hover:text-[#e0eaff]">Annuler</button>
          </div>
          {error && (
            <div className="mb-2 p-2 rounded-md bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-xs text-rose-700 dark:text-rose-300">
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              value={editingDraft}
              onChange={e => setEditingDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleEditSubmit(e);
                } else if (e.key === 'Escape') {
                  handleEditCancel();
                }
              }}
              autoFocus
              aria-label="Modifier le message"
              placeholder="Modifier le contenu du message…"
              className="orion-settings-input flex-1 rounded-xl px-3 py-2 text-sm resize-none"
              rows={2}
              maxLength={4000}
            />
            <button type="submit" disabled={!editingDraft.trim()}
              className="orion-settings-primary-btn text-white font-bold px-4 rounded-xl text-sm disabled:opacity-50 self-stretch">
              Enregistrer
            </button>
          </div>
        </form>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="border-t border-slate-200 dark:border-[rgba(100,140,210,0.2)] px-4 py-3 flex-shrink-0 bg-white dark:bg-transparent">
        {error && !editingMessageId && (
          <div className="mb-2 p-2 rounded-md bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-xs text-rose-700 dark:text-rose-300">
            {error}
          </div>
        )}
        {/* Pièces jointes en attente */}
        {pendingAttachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingAttachments.map(a => (
              <div key={a.id} className="flex items-center gap-2 text-[11px] rounded-md px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <span>📎 {a.original_name}</span>
                <button type="button"
                  onClick={() => removePendingAttachment(a.id)}
                  aria-label="Retirer cette pièce jointe"
                  className="text-rose-500 hover:text-rose-700 font-bold"
                >×</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          {/* Input fichier caché */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            title="Joindre des fichiers"
            aria-label="Joindre des fichiers"
            className="hidden"
            onChange={handleFileSelected}
            disabled={isReadOnly || sending || uploading}
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
          />
          {/* Bouton trombone */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isReadOnly || sending || uploading || pendingAttachments.length >= 10}
            aria-label="Joindre un fichier"
            title="Joindre un fichier"
            className="self-stretch px-3 rounded-xl text-sm border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            {uploading ? '⏳' : '📎'}
          </button>
          <textarea
            value={input}
            onChange={e => {
              setInput(e.target.value);
              if (e.target.value.length > 0) notifyTyping();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder={isReadOnly ? 'Lecture seule — vous ne pouvez pas écrire' : 'Écrire un message… (Entrée = envoyer, Maj+Entrée = saut de ligne)'}
            disabled={isReadOnly || sending}
            className="orion-settings-input flex-1 rounded-xl px-3 py-2 text-sm resize-none disabled:opacity-50"
            rows={2}
            maxLength={4000}
          />
          <button
            type="submit"
            disabled={isReadOnly || sending || (!input.trim() && pendingAttachments.length === 0)}
            className="orion-settings-primary-btn text-white font-bold px-4 rounded-xl text-sm disabled:opacity-50 self-stretch"
          >
            {sending ? '…' : 'Envoyer'}
          </button>
        </div>
        <div className="text-[9px] text-slate-400 dark:text-[#546a96] mt-1 text-right">{input.length} / 4000</div>
      </form>
    </div>
  );
};

export default ConversationView;
